import { useState } from 'react'
import { TarotCard } from '../components/TarotCard'
import { haptic } from '../lib/telegram'
import { useSirr } from '../store'
import type { DrawnCard } from '../lib/storage'

interface Props {
  /** Колода, разложенная перед человеком: карты уже вытянуты, он выбирает одну. */
  deck: DrawnCard[]
  onPick: (card: DrawnCard) => void
}

/**
 * Выбор карты из разложенной колоды — для расклада «Да или нет».
 *
 * Карты вытянуты заранее (`drawCards` в App), человек выбирает не карту, а
 * место в вееере: так выбор остаётся его, а расклад — детерминированным.
 * Между нажатием и переходом держим паузу, чтобы выбранная карта успела
 * подняться из веера: без неё расклад открывается рывком и ощущение
 * «я выбрал именно эту» теряется.
 */
export function PickCard({ deck, onPick }: Props) {
  const { t } = useSirr()
  const [picked, setPicked] = useState<number | null>(null)

  const choose = (i: number) => {
    if (picked !== null) return
    setPicked(i)
    haptic.impact('medium')
    setTimeout(() => onPick(deck[i]), 620)
  }

  // Дуга пологая и широкая: угол мал, но центр вращения далеко внизу, поэтому
  // соседние карты расходятся примерно на четверть своей ширины.
  const step = 32 / Math.max(1, deck.length - 1)
  const mid = (deck.length - 1) / 2

  return (
    <div className="deckpick">
      <div className="deckpick__intro">
        <div className="deckpick__wish">{t('pickWish')}</div>
        <div className="deckpick__hint">{t('pickHint')}</div>
      </div>

      <div className="deckpick__rule" aria-hidden="true">
        <span />
        <b>{'\u2726\ufe0e'}</b>
        <span />
      </div>

      <div className="deckpick__status">{picked !== null ? t('pickChosen') : t('pickTitle')}</div>

      <div className="deckpick__fan" role="group" aria-label={t('pickTitle')}>
        {deck.map((_, i) => {
          const angle = (i - mid) * step
          const isPicked = picked === i
          return (
            <button
              key={i}
              type="button"
              className={`deckpick__slot${isPicked ? ' is-picked' : ''}${picked !== null && !isPicked ? ' is-faded' : ''}`}
              style={{
                transform: `rotate(${angle}deg)`,
                zIndex: isPicked ? 20 : i,
              }}
              onClick={() => choose(i)}
              aria-label={`${t('pickTitle')} ${i + 1}`}
            >
              <TarotCard faceUp={false} sym="✦" title="" width={110} height={174} showTitle={false} />
            </button>
          )
        })}
      </div>
    </div>
  )
}
